import { format } from 'date-fns';
import { getReasonCodes } from 'reducers/reasonCodesDB';
import { REASONS } from 'constants/reasonCodesDB';
import { PosPlugin } from 'plugins/plugin';
import {
  CashInOutPrintPayload,
  CashInOutPrintExtra,
} from 'actions/integrations/printer/preparePrintoutData';
import { PatchScript } from '@pos-refacto/patchscript-with-react';

function generateWbuPatchscript(
  props: CashInOutPrintPayload & CashInOutPrintExtra & { reason: string },
): PatchScript {
  const date = format(new Date(), 'MM/dd/yyyy HH:mm:ss');
  const printInfo = { ...props, date };
  const notesColumnWidth = Math.max(
    printInfo.translations.defaults.notesTitle.length,
    'Reason'.length,
  ) + 1
  const reasonAndAmountColumnWidth = Math.max(printInfo.sum.length, printInfo.reason.length) + 1;

  return [
    {
      pieces: [
        { meta: { bold: true, size: 2 }, text: printInfo.companyAndWarehouse },
      ],
      type: 'text',
    },
    { pieces: [{ text: ' ' }], type: 'text' },
    {
      pieces: [{ text: printInfo.address }],
      type: 'text',
    },
    { pieces: [{ text: printInfo.date }], type: 'text' },
    {
      pieces: [
        { text: printInfo.translations.sale.employee },
        {
          text: ': ',
          meta: {
            bold: false,
            underline: false,
          },
        },
        { text: printInfo.employee.employeeName },
      ],
      type: 'text',
    },
    { pieces: [{ text: ' ' }], type: 'text' },
    {
      columns: [
        {
          baseWidth: notesColumnWidth,
          weight: 0,
        },
        { baseWidth: 0, weight: 1 },
        { baseWidth: reasonAndAmountColumnWidth, weight: 0 },
      ],
      rows: [
        {
          cells: [
            {
              pieces: [
                {
                  text: printInfo.translations.defaults.notesTitle,
                },
              ],
            },
            {
              align: 'right',
              pieces: [
                {
                  text: printInfo.comment,
                  meta: {
                    bold: false,
                    underline: false,
                  },
                },
              ],
            },
            'colspan',
          ],
          type: 'normal',
        },
        {
          cells: [
            'colspan',
            {
              align: 'left',
              pieces: [],
            },
            {
              align: 'left',
              pieces: [],
            },
          ],
          type: 'sectionStart',
        },
        {
          cells: [
            {
              pieces: [
                {
                  text: 'Reason',
                },
              ],
            },
            {
              align: 'right',
              pieces: [
                {
                  text: printInfo.reason,
                  meta: {
                    bold: false,
                    underline: false,
                  },
                },
              ],
            },
            'colspan',
          ],
          type: 'normal',
        },
        {
          cells: [
            'colspan',
            {
              align: 'left',
              pieces: [],
            },
            {
              align: 'left',
              pieces: [],
            },
          ],
          type: 'normal',
        },
        {
          cells: [
            null,
            {
              align: 'center',
              pieces: [{ meta: { bold: true }, text: printInfo.cashInOutText }],
            },
            {
              align: 'right',
              pieces: [{ meta: { bold: true }, text: printInfo.sum }],
            },
          ],
          type: 'normal',
        },
      ],
      type: 'table',
    },
  ];
}

const getCashInOutPrint: Required<PosPlugin>['onCashInOutPrint']['on'] = (
  p,
  { data },
) => async (dispatch, getState) => {
  const reasonCodes = getReasonCodes(
    data.type === 'in' ? REASONS.CASH_IN : REASONS.CASH_OUT,
  )(getState());
  const reasonCodeName =
    reasonCodes.find(r => r.reasonID === data.reasonID)?.name ?? '';
  const newData = { ...data, reason: reasonCodeName };
  const patchScript = generateWbuPatchscript(newData);
  return {
    data: { ...data, reason: reasonCodeName },
    patchScript,
  };
};

export default getCashInOutPrint;
